import { useEffect, useRef, useState } from 'react'
import { HelpCircle } from 'lucide-react'
import { isHttpUrl } from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'

interface ListingsFallbackLinkControlProps {
  /** Current company-level fallback link (source of truth), or null when unset. */
  fallbackLink: string | null
  loading?: boolean
  /** Persist the new value. Pass null to clear it. Throws on failure. */
  onSave: (link: string | null) => Promise<void>
}

/**
 * Edits the company-level listings fallback link — the URL texted to callers
 * when no matching listing can be offered (independent of the sync source: URL,
 * PMS, or CSV). The value is stored on the Twenty CRM Company object; this
 * control is just its editor, shown between the listings toolbar and the table
 * (mirroring the on-call calendar's "Fallback" control).
 *
 * You simply type/paste a URL; a floating save bar appears (the same
 * FloatingActionIsland pattern the company settings form uses) so there is no
 * inline save button. Requires a FloatingActionIslandProvider ancestor.
 */
export function ListingsFallbackLinkControl({
  fallbackLink,
  loading = false,
  onSave,
}: ListingsFallbackLinkControlProps) {
  const [value, setValue] = useState(fallbackLink ?? '')
  const [saving, setSaving] = useState(false)
  // The persisted value we last synced into the input. Used to tell an unsaved
  // edit apart from a fresh value arriving from the parent.
  const lastSyncedRef = useRef(fallbackLink ?? '')

  // Adopt a new persisted value from the parent only when the user has no
  // unsaved edit in progress (i.e. the input still matches what we last synced).
  // Otherwise a parent re-render would silently clobber what they typed.
  useEffect(() => {
    const incoming = fallbackLink ?? ''
    // Capture the previously-synced value before overwriting the ref, so the
    // functional update below compares against the OLD value even if it runs
    // after this effect returns.
    const lastSynced = lastSyncedRef.current
    lastSyncedRef.current = incoming
    setValue(prev => (prev === lastSynced ? incoming : prev))
  }, [fallbackLink])

  const trimmed = value.trim()
  // Empty is allowed (clears the link); a non-empty value must be an http(s) URL.
  const invalid = trimmed.length > 0 && !isHttpUrl(trimmed)
  const dirty = trimmed !== (fallbackLink ?? '')

  const handleSave = async () => {
    if (!dirty || invalid || saving) return
    try {
      setSaving(true)
      await onSave(trimmed.length > 0 ? trimmed : null)
    } catch {
      // The caller surfaces the error toast; keep the entered value to retry.
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
      <span className="flex h-9 items-center gap-1.5">
        <span className="text-sm font-medium">Fallback link</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="What is the listings fallback link?"
                className="text-muted-foreground hover:text-foreground"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              Texted to callers when no matching listing can be offered. Applies
              to URL, PMS, and CSV listings.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>

      <div className="flex flex-col gap-1">
        {loading ? (
          <Skeleton className="h-9 w-72" />
        ) : (
          <Input
            type="url"
            placeholder="https://example.com/listings"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleSave()
              }
            }}
            aria-invalid={invalid}
            className="w-72"
          />
        )}
        {invalid && (
          <p className="text-xs text-destructive">
            Enter a valid URL starting with http:// or https://
          </p>
        )}
      </div>

      <FloatingActionIslandContent
        show={dirty}
        componentId="listings-fallback-link-save"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl items-center">
          <div className="mr-4 flex flex-grow items-center whitespace-nowrap text-sm text-gray-600 pointer-events-none">
            <span className="font-medium">Link ready to save</span>
          </div>
          <div className="flex items-center justify-end pointer-events-auto">
            <Button
              onClick={handleSave}
              disabled={saving || invalid}
              size="sm"
              variant="form"
            >
              {saving ? 'Saving...' : 'Save link'}
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}
